import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import useNotifications from 'Pods/Shared/Hooks/useNotifications'
import useSentry from 'Pods/Shared/Hooks/useSentry'
import I18n from 'Pods/Shared/I18n'

import { storePendingToast } from 'Utils/pendingToast'
import { NOOP } from 'Utils/stableDefaults'

import { CHECKOUT_CAPTCHA_ID } from '../Constants/captcha'
import { SUBSCRIPTION_PAYMENT_FORM_PATH } from '../Constants/paths'
import { buildPlansFromBackend, DEFAULT_PLAN_KEY, PLANS } from '../Constants/plans'

function writePlanParam(planKey) {
  const url = new URL(window.location.href)
  url.searchParams.set('plan', planKey)
  window.history.replaceState(null, '', url.toString())
}

export const STEP_EMAIL = 'email'
export const STEP_LOGIN = 'login'
export const STEP_CAPTCHA = 'captcha'
export const STEP_PAYMENT = 'payment'
export const STEP_CREATE_ACCOUNT = 'create_account'

const DEFAULT_RETURN_PATH = '/profile/settings#tab-settings-subscription'
const DEFAULT_GUEST_CHECKOUT_PATH = '/guests/sessions'

export default function useSubscriptionCheckout({
  plans: bePlans = null,
  orderSummary = null,
  paymentIntent: paymentIntentProp = '',
  signature: signatureProp = '',
  savedCompanyData = null,
  loginRequired = false,
  captchaRequired = false,
  locale = 'en',
  returnPath = DEFAULT_RETURN_PATH,
  guestCheckoutPath = DEFAULT_GUEST_CHECKOUT_PATH,
  isGuest = false,
  initialGuestEmail = null,
  subscriptionTier = 'essentials',
  isPlansRestructure = false,
}) {

  const hasBackendPlans = Array.isArray(bePlans) && bePlans.length > 0
  const isUpdateMode = !orderSummary && !hasBackendPlans

  const [step, setStep] = useState(() => {
    if (loginRequired) return STEP_EMAIL
    if (captchaRequired) return STEP_CAPTCHA
    return STEP_PAYMENT
  })
  const [guestEmail, setGuestEmail] = useState(initialGuestEmail)
  const [guestPaymentData, setGuestPaymentData] = useState(null)
  const [isExistingUser, setIsExistingUser] = useState(false)

  const [paymentForm, setPaymentForm] = useState(null)
  // Holds the most recent intent requested before the SDK was ready — flushed by handleFormReady.
  const pendingIntentRef = useRef(null)
  const [isFormValid, setIsFormValid] = useState(false)
  const [isIntentSyncFailed, setIsIntentSyncFailed] = useState(false)
  const [isIntentUpdating, setIsIntentUpdating] = useState(false)
  const latestIntentUpdateRef = useRef(0)
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false)
  const [vatAmount, setVatAmount] = useState(orderSummary?.vatAmount || 0)
  const [isCompanyToggleOn, setIsCompanyToggleOn] = useState(!!savedCompanyData)
  const [isCompanyApplied, setIsCompanyApplied] = useState(false)

  const [currentPlans, setCurrentPlans] = useState(() => buildPlansFromBackend(bePlans))
  const [selectedPlanKey, setSelectedPlanKey] = useState(() => {
    const merged = buildPlansFromBackend(bePlans)
    return (merged.find((p) => p.isDefault) || merged[0] || { key: DEFAULT_PLAN_KEY }).key
  })

  // Legacy path ships VAT only for the default plan; derive a rate to apply to the selected one.
  const taxRate = useMemo(() => {
    if (!orderSummary?.currentPrice) return 0
    return (vatAmount || 0) / orderSummary.currentPrice
  }, [orderSummary, vatAmount])

  const planSummary = useMemo(() => {
    const plan = currentPlans.find((p) => p.key === selectedPlanKey) || currentPlans[0] || PLANS[0]
    const planVat = plan.vatAmount != null
      ? plan.vatAmount
      : +(plan.price * taxRate).toFixed(2)

    return {
      planKey: plan.key,
      currentPrice: plan.price,
      originalPrice: plan.originalPrice ?? null,
      vatAmount: planVat,
      cadence: plan.cadence,
      renewalIntervalMonths: plan.renewalIntervalMonths,
      paymentIntent: plan.paymentIntent ?? null,
      signature: plan.signature ?? null,
    }
  }, [selectedPlanKey, currentPlans, taxRate])

  const { showError } = useNotifications()
  const { captureException } = useSentry()

  const t = useCallback(
    (key, options = {}) => I18n.t(key, { ns: 'rails', lng: locale, ...options }),
    [locale]
  )

  // Until Solidgate confirms the new intent the iframe still charges the previous one, so Pay stays
  // blocked while an update is in flight and after one fails. Only the latest update may change that state.
  const updateSolidgateIntent = useCallback((form, config, onFailure = NOOP) => {
    latestIntentUpdateRef.current += 1
    const updateId = latestIntentUpdateRef.current
    setIsIntentUpdating(true)

    form.update(config)
      .then(() => {
        if (updateId !== latestIntentUpdateRef.current) return

        setIsIntentUpdating(false)
        setIsIntentSyncFailed(false)
      })
      .catch((error) => {
        const code = error?.code || error?.error?.code || 'unknown'
        captureException(new Error(`Solidgate update failed: ${code}`), { extra: { code, message: error?.message } })
        if (updateId !== latestIntentUpdateRef.current) return

        setIsIntentUpdating(false)
        setIsIntentSyncFailed(true)
        showError(t('subscription_checkout.intent_update_error'))
        onFailure()
      })
  }, [captureException, showError, t])

  const swapSolidgateIntent = useCallback((intent, signature, onFailure = NOOP) => {
    if (!intent || !signature) return
    if (paymentForm) {
      updateSolidgateIntent(paymentForm, { partialIntent: intent, signature }, onFailure)
    } else {
      // Keep the first rollback: it restores the plan the iframe was initialized with.
      pendingIntentRef.current = {
        config: { partialIntent: intent, signature },
        onFailure: pendingIntentRef.current?.onFailure || onFailure,
      }
    }
  }, [paymentForm, updateSolidgateIntent])

  // Frozen on first mount — re-assigning paymentIntent/signature props re-inits the iframe (visible flash).
  // Plan switches and VAT updates go through paymentForm.update(...) instead.
  const [stableSdkIntent, setStableSdkIntent] = useState(() => {
    if (paymentIntentProp && signatureProp) {
      return { paymentIntent: paymentIntentProp, signature: signatureProp }
    }
    if (hasBackendPlans) {
      const initialPlans = buildPlansFromBackend(bePlans)
      const def = initialPlans.find((p) => p.isDefault) || initialPlans[0]
      if (def?.paymentIntent && def?.signature) {
        return { paymentIntent: def.paymentIntent, signature: def.signature }
      }
    }
    return null
  })
  useEffect(() => {
    if (stableSdkIntent) return
    const def = currentPlans.find((p) => p.isDefault) || currentPlans[0]
    if (def?.paymentIntent && def?.signature) {
      setStableSdkIntent({ paymentIntent: def.paymentIntent, signature: def.signature })
    }
  }, [currentPlans, stableSdkIntent])

  const applyMergedPlans = useCallback((mergedPlans) => {
    setCurrentPlans(mergedPlans)
    const stillValid = mergedPlans.some((p) => p.key === selectedPlanKey)
    const nextKey = stillValid
      ? selectedPlanKey
      : (mergedPlans.find((p) => p.isDefault) || mergedPlans[0]).key

    if (!stillValid) setSelectedPlanKey(nextKey)

    const active = mergedPlans.find((p) => p.key === nextKey)
    if (active?.paymentIntent && active?.signature) {
      swapSolidgateIntent(active.paymentIntent, active.signature)
    }
    return active || null
  }, [selectedPlanKey, swapSolidgateIntent])

  const handlePlanChange = useCallback((planKey) => {
    const plan = currentPlans.find((p) => p.key === planKey)
    // Re-selecting the current plan after a failed update retries it.
    if (!plan || (plan.key === selectedPlanKey && !isIntentSyncFailed)) return

    const previousPlanKey = selectedPlanKey
    setSelectedPlanKey(plan.key)
    if (isPlansRestructure) writePlanParam(plan.key)
    swapSolidgateIntent(plan.paymentIntent, plan.signature, () => {
      setSelectedPlanKey(previousPlanKey)
      if (isPlansRestructure) writePlanParam(previousPlanKey)
    })
  }, [currentPlans, selectedPlanKey, isIntentSyncFailed, isPlansRestructure, swapSolidgateIntent])

  const { deleteData: destroyGuestSession } = useApiDelete()
  const { getData: fetchPaymentIntent, isLoading: isFetchingPaymentData } = useApiGet()

  // --- Fetch payment data from server ---
  const fetchPaymentData = useCallback((captchaToken = null) => {
    let path = returnPath && returnPath !== DEFAULT_RETURN_PATH
      ? `${SUBSCRIPTION_PAYMENT_FORM_PATH}?return_url=${encodeURIComponent(returnPath)}`
      : SUBSCRIPTION_PAYMENT_FORM_PATH

    // Carry the tier so the refetch recreates the same plan; dropping it makes the
    // backend fall back to Essentials and bills the wrong intent under Premium copy.
    if (subscriptionTier && subscriptionTier !== 'essentials') {
      path += path.includes('?') ? `&tier=${subscriptionTier}` : `?tier=${subscriptionTier}`
    }

    // Without the term the backend defaults to monthly and the form mounts on that intent before swapping.
    if (isPlansRestructure) {
      path += `${path.includes('?') ? '&' : '?'}plan=${selectedPlanKey}`
    }

    if (captchaToken) {
      path += `${path.includes('?') ? '&' : '?'}MTCaptchaToken=${encodeURIComponent(captchaToken)}`
    }

    fetchPaymentIntent({
      path,
      onSuccess: ({ data }) => {
        if (data.redirectTo) {
          const type = data.flashType === 'notice' ? 'success' : 'error'
          storePendingToast({ type, message: data.message })
          window.location.href = data.redirectTo
          return
        }

        // Guest session was lost between email submit and fetch: return to email step.
        if (data.loginRequired) {
          setGuestEmail(null)
          setGuestPaymentData(null)
          setStep(STEP_EMAIL)
          return
        }

        // Velocity lockout gate: the server withholds the signed payment intent
        // until the guest solves the captcha (see VerifyGuestCheckoutCaptcha).
        if (data.captchaRequired) {
          if (captchaToken) {
            // The token we sent was rejected (expired/invalid): reset the widget.
            window.mtcaptcha?.resetUI?.(CHECKOUT_CAPTCHA_ID)
            showError(t('subscription_checkout.captcha_step.failed'))
          }
          setStep(STEP_CAPTCHA)
          return
        }

        setGuestPaymentData({
          merchant: data.merchant,
          signature: data.signature,
          paymentIntent: data.paymentIntent,
          plans: data.plans,
          companyPurchaseEnabled: data.companyPurchaseEnabled,
          companyFormData: data.companyFormData,
          savedCompanyData: data.savedCompanyData,
          subscriptionV16Enabled: data.subscriptionV16Enabled,
          subscriptionV17CatalogEnabled: data.subscriptionV17CatalogEnabled,
          subscriptionTier: data.subscriptionTier,
          premiumRenewalDate: data.premiumRenewalDate,
        })

        if (Array.isArray(data.plans) && data.plans.length > 0) {
          applyMergedPlans(buildPlansFromBackend(data.plans))
        }

        if (data.orderSummary) {
          setVatAmount(data.orderSummary.vatAmount || 0)
        }

        setStep(STEP_PAYMENT)
      },
      onError: () => showError(t('subscription_checkout.payment_error_toast')),
    })
  }, [
    fetchPaymentIntent,
    applyMergedPlans,
    returnPath,
    subscriptionTier,
    isPlansRestructure,
    selectedPlanKey,
    showError,
    t,
  ])

  // --- Step handlers ---
  const handleEmailSubmit = useCallback((email, isNewUser) => {
    setGuestEmail(email)

    if (isNewUser) {
      setIsExistingUser(false)
      fetchPaymentData()
    } else {
      setIsExistingUser(true)
      setStep(STEP_LOGIN)
    }
  }, [fetchPaymentData])

  const handleLoginSuccess = useCallback(() => {
    fetchPaymentData()
  }, [fetchPaymentData])

  const handleLoginClick = useCallback(() => {
    setIsExistingUser(false)
    setStep(STEP_LOGIN)
  }, [])

  const handleCaptchaSubmit = useCallback((captchaToken) => {
    fetchPaymentData(captchaToken)
  }, [fetchPaymentData])

  const resetGuestCheckout = useCallback(() => {
    setStep(STEP_EMAIL)
    setGuestEmail(null)
    setGuestPaymentData(null)
    setPaymentForm(null)
    setIsFormValid(false)
    setIsIntentSyncFailed(false)
    setIsIntentUpdating(false)
    setIsExistingUser(false)
  }, [])

  const handleGuestReset = useCallback(() => {
    destroyGuestSession({
      path: guestCheckoutPath,
      onSuccess: resetGuestCheckout,
      onError: () => showError(t('subscription_checkout.payment_error_toast')),
    })
  }, [destroyGuestSession, guestCheckoutPath, resetGuestCheckout, showError, t])

  // --- Payment form handlers ---
  const handleFormReady = useCallback((form) => {
    setPaymentForm(form)
    if (pendingIntentRef.current) {
      const { config, onFailure } = pendingIntentRef.current
      updateSolidgateIntent(form, config, onFailure)
      pendingIntentRef.current = null
    }
  }, [updateSolidgateIntent])

  const handleFormValidityChange = useCallback((valid) => {
    setIsFormValid(valid)
  }, [])

  const submitPaymentForm = useCallback(() => {
    if (paymentForm) {
      paymentForm.submit()
    }
  }, [paymentForm])

  const handlePaymentProcessing = useCallback(() => {
    setIsPaymentProcessing(true)
  }, [])

  const isGuestCheckout = (isGuest || Boolean(guestEmail)) && !isExistingUser

  const handlePaymentSuccess = useCallback(() => {
    if (!isGuestCheckout) return
    setStep(STEP_CREATE_ACCOUNT)
  }, [isGuestCheckout])

  const handleAccountCompleted = useCallback((redirectTo) => {
    window.location.href = redirectTo || returnPath
  }, [returnPath])

  // --- Company details handlers ---
  const handleCompanyApplied = useCallback(({
    vatAmount: newVat,
    paymentIntent: newIntent,
    signature: newSignature,
    plans: newPlans,
  }) => {
    setIsCompanyApplied(true)

    if (Array.isArray(newPlans) && newPlans.length > 0) {
      const active = applyMergedPlans(buildPlansFromBackend(newPlans))
      if (active?.vatAmount != null) setVatAmount(active.vatAmount)
      return
    }

    if (newVat !== undefined) setVatAmount(newVat)
    if (newIntent && newSignature && paymentForm) {
      updateSolidgateIntent(paymentForm, { partialIntent: newIntent, signature: newSignature })
    }
  }, [paymentForm, applyMergedPlans, updateSolidgateIntent])

  const handleCompanyReverted = useCallback((data = {}) => {
    setIsCompanyApplied(false)

    if (Array.isArray(data.plans) && data.plans.length > 0) {
      const active = applyMergedPlans(buildPlansFromBackend(data.plans))
      if (active?.vatAmount != null) setVatAmount(active.vatAmount)
      return
    }

    setVatAmount(data.vatAmount !== undefined ? data.vatAmount : (orderSummary?.vatAmount || 0))
    if (data.paymentIntent && data.signature && paymentForm) {
      updateSolidgateIntent(paymentForm, { partialIntent: data.paymentIntent, signature: data.signature })
    }
  }, [orderSummary, paymentForm, applyMergedPlans, updateSolidgateIntent])

  const handleCompanyToggleChange = useCallback((on) => {
    setIsCompanyToggleOn(on)
  }, [])

  // Always written (even for the default) so a stale ?plan=annual can't survive a later plan switch.
  const handleRetry = useCallback(() => writePlanParam(selectedPlanKey), [selectedPlanKey])

  const isPayDisabled = !isFormValid
    || isIntentUpdating
    || isIntentSyncFailed
    || (isCompanyToggleOn && !isCompanyApplied)
  const isPaymentStep = step === STEP_PAYMENT

  return {
    // step state
    step,
    guestEmail,
    isExistingUser,
    isGuestCheckout,
    isPaymentStep,
    isUpdateMode,

    // raw guest fetch payload (component merges it with SSR props)
    guestPaymentData,

    // derived flags
    isFormValid,
    isIntentSyncFailed,
    isIntentUpdating,
    isPaymentProcessing,
    isPayDisabled,
    isFetchingPaymentData,
    vatAmount,

    // plan selection
    plans: currentPlans,
    selectedPlanKey,
    planSummary,
    handlePlanChange,
    stableSdkIntent,

    // handlers
    handleEmailSubmit,
    handleLoginSuccess,
    handleLoginClick,
    handleCaptchaSubmit,
    handleGuestReset,
    handleFormReady,
    handleFormValidityChange,
    submitPaymentForm,
    handlePaymentProcessing,
    handlePaymentSuccess,
    handleAccountCompleted,
    handleCompanyApplied,
    handleCompanyReverted,
    handleCompanyToggleChange,
    handleRetry,
  }
}
